<template>
  <div :class="$style.base">
    <PageWrap v-if="!loading">
      <template #top>
        <BaseAlert
          v-if="showIntegrationWarning"
          color="warning"
          :text="warningText"
          size="s"
          v-test="'google-reserve-settings-alert'"
          :primaryAction="{
            text: t('google_reserve.banner.update_settings'),
            routerLink: { name: 'admin-online-bookings' }
          }"
        />
        <BaseAlert
          v-if="!isEnabled && hasUnelegiblePlan"
          color="info"
          :text="t('google_reserve.banner.upgrade_warning')"
          :primaryAction="{
            text: t('google_reserve.banner.upgrade_button'),
            routerLink: { name: 'subscription' }
          }"
          size="s"
          v-test="'google-reserve-upgrade-alert'"
        />
      </template>
      <template #left>
        <BaseLabel :color="isEnabled ? 'success' : 'error'">
          {{ isEnabled ? t('google_reserve.banner.connected') : t('google_reserve.banner.disconnected') }}
        </BaseLabel>
        <BaseHeading size="xl">
          {{ t('google_reserve.banner.title') }}
        </BaseHeading>
        <BaseText>{{ t('google_reserve.banner.description') }}</BaseText>
        <Flex
          direction="column"
          :gap="0.5"
        >
          <Flex
            :gap="0.5"
            align="center"
            v-for="item in checklist"
            :key="item"
            :class="$style.checklistItem"
          >
            <BaseIcon
              name="circle-check"
              color="primary"
              size="s"
            />
            <BaseText size="s">{{ item }}</BaseText>
          </Flex>
        </Flex>
      </template>
      <template #cta>
        <BaseButton
          v-if="isEnabled"
          color="inverted"
          @click="openModal = true"
          v-test="'google-reserve-settings'"
        >
          {{ t('google_reserve.banner.view_settings') }}
        </BaseButton>
        <BaseButton
          v-else
          @click="onConnectClick"
          v-test="'google-reserve-connect'"
        >
          {{ t('google_reserve.banner.primary_action') }}
        </BaseButton>
      </template>
      <template #links>
        <BaseText
          :href="
            helpCenterArticleUrl(
              $t('integrations.google_reserve.settings.help_article_url')
            )
          "
          v-test="'google-reserve-learn-more'"
        >
          {{ t('google_reserve.banner.secondary_action') }}
        </BaseText>
      </template>
      <template #right>
        <img src="/img/online/google-reserve.svg">
      </template>
      <template #bottom>
        <div :class="$style.faqHeading">
          <BaseHeading size="l">
            {{ t('google_reserve.faq.heading') }}
          </BaseHeading>
        </div>
        <FAQ />
      </template>
    </PageWrap>
    <SettingsModal
      v-if="openModal"
      @close="openModal = false"
      @onSaved="onSaved"
    />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { useGoogleReserveStore } from '@/stores/google-reserve';
import PageWrap from '../PageWrap.vue';
import FAQ from './FAQ.vue';
import SettingsModal from './SettingsModal.vue';

const {
  doesNotAllowNewCustomers,
  requireConfirmation,
  isEnabled,
  hasUnelegiblePlan,
  loading,
} = storeToRefs(useGoogleReserveStore());

const { t } = useI18n();

const checklist = [
  t('google_reserve.banner.checklist.one'),
  t('google_reserve.banner.checklist.two'),
  t('google_reserve.banner.checklist.three'),
];

const openModal = ref(false);

const onConnectClick = () => {
  openModal.value = true;
};

const onSaved = () => {
  if (!isEnabled.value) {
    isEnabled.value = true;
  }
};

const showIntegrationWarning = computed(() => {
  return (
    isEnabled.value &&
    (doesNotAllowNewCustomers.value || requireConfirmation.value)
  );
});

const warningText = computed(() => {
  if (doesNotAllowNewCustomers.value && requireConfirmation.value) {
    return t('google_reserve.banner.setting_warning');
  } else if (doesNotAllowNewCustomers.value) {
    return t('google_reserve.banner.setting_warning_customers');
  } else if (requireConfirmation.value) {
    return t('google_reserve.banner.setting_warning_confirmation');
  }
  return '';
});
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;
}

.checklistItem {
  text-align: left;
}

.faqHeading {
  align-self: center;
}
</style>
